import { Entity, entityTagToCountry, formatTicker } from '@api';
import { Icon } from '@design';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { NEW_YORK_TIMEZONE } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { LineData } from '@terminal/modules/market-reaction-charts/types';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatProportionChange } from '../utils';
import styles from './CatalystMultipleMarketReactionPrice.module.scss';

export interface CatalystMultipleMarketReactionPriceProps {
  entities: Entity[];
  lines: LineData[];
  lineColors: string[];
  isWindowComplete: boolean;
  isAfterHoursChart?: boolean;
}

export const CatalystMultipleMarketReactionPrice = ({
  entities,
  lines,
  lineColors,
  isWindowComplete,
  isAfterHoursChart,
}: CatalystMultipleMarketReactionPriceProps) => {
  const { t } = useTranslation(['insights']);

  const latestDataPointTime = lines.reduce<string | undefined>(
    (latest, line) => {
      const time = line.data[line.data.length - 1]?.time;

      if (!time) {
        return latest;
      }

      return !latest || time > latest ? time : latest;
    },
    undefined
  );

  const referenceDate = latestDataPointTime ?? lines[0]?.prevCloseData?.time;

  const getDateLabel = () => {
    if (!referenceDate) {
      return null;
    }

    if (isWindowComplete) {
      if (isAfterHoursChart) {
        return t('insights:catalyst.drawer.asOfDate', {
          date: `${formatInTimeZone(
            referenceDate,
            NEW_YORK_TIMEZONE,
            'MMM d, h:mm a'
          )} ET`,
        });
      }

      return t('insights:catalyst.drawer.asOfDateClose', {
        date: formatInTimeZone(referenceDate, 'UTC', 'MMM d'),
      });
    }

    return t('insights:catalyst.drawer.asOfDate', {
      date: `${formatInTimeZone(
        referenceDate,
        NEW_YORK_TIMEZONE,
        'MMM d, h:mm a'
      )} ET`,
    });
  };

  const dateLabel = getDateLabel();

  return (
    <>
      {dateLabel && <span className={styles.dateSpan}>{dateLabel}</span>}
      <div className={styles.legendWrapper}>
        {entities.map((entity, index) => {
          const currentLine = lines[index];
          const countryCode = entityTagToCountry[entity.tag];
          return (
            <div className={styles.legendRow} key={entity.tag}>
              <div className={styles.legendItem}>
                <div className={styles.iconWrapper}>
                  <div
                    className={styles.legendItemVector}
                    style={{ borderColor: lineColors[index] }}
                  />
                  {countryCode ? (
                    <FlagIcon
                      countryCode={countryCode}
                      variant="circular"
                      customSize={16}
                    />
                  ) : (
                    <Icon iconName="Globe" size={16} viewBox="4 4 32 32" />
                  )}
                </div>
                <div className={styles.entityDetails}>
                  <TooltipContainer label={entity.name_short ?? ''}>
                    <span className={styles.entityName}>
                      {entity.name_short}
                    </span>
                  </TooltipContainer>
                  <span className={styles.entityTag}>
                    {formatTicker(entity)}
                  </span>
                </div>
              </div>
              {currentLine.lastProportionChange !== undefined && (
                <span
                  className={clsx(styles.proportionChange, {
                    [styles.positive]: currentLine.lastProportionChange > 0,
                    [styles.negative]: currentLine.lastProportionChange < 0,
                  })}
                >
                  {formatProportionChange(currentLine.lastProportionChange)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
